import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Select, MenuItem,
  FormControl, InputLabel, Stack, Alert, CircularProgress, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Autocomplete, Divider, Chip, Tabs, Tab, Tooltip, Switch, FormControlLabel,
  ToggleButton, ToggleButtonGroup, Dialog, DialogTitle, DialogContent, DialogActions,
  InputAdornment,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  History as HistoryIcon,
  RequestQuote as QuoteIcon,
  Tune as TemplateIcon,
  Edit as EditIcon,
  ReceiptLong as InvoiceIcon,
  CheckCircle as AcceptIcon,
  Cancel as RejectIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';

// ─────────────────────────────────────────────────────────────────────────────
// Tipos
// ─────────────────────────────────────────────────────────────────────────────

interface QuoteItem {
  description: string;
  quantity: number;
  unitPrice: number;
}

interface ClientOption {
  _id: string;
  name: string;
  email?: string;
  phone?: string;
  address?: { street?: string; city?: string };
}

interface Template {
  _id: string;
  name: string;
  description?: string;
  items?: QuoteItem[];
  includes: string[];
  warranty: string[];
  conditions: string[];
  paymentInfo?: string;
  notes?: string;
  itbisEnabled: boolean;
  itbisRate: number;
  validityDays: number;
  isDefault: boolean;
}

interface SavedQuote {
  _id: string;
  quoteNumber: string;
  clientId?: { _id: string; name?: string };
  prospect?: { name?: string; company?: string; phone?: string; email?: string; address?: string };
  items: QuoteItem[];
  discount?: { rate: number; amount: number; label?: string };
  itbis: { enabled: boolean; rate: number; amount: number };
  subtotal: number;
  total: number;
  issueDate: string;
  validUntil: string;
  includes: string[];
  warranty: string[];
  conditions: string[];
  notes?: string;
  paymentInfo?: string;
  status: string;
  vencida?: boolean;
  invoiceId?: any;
}

const ESTADOS: Record<string, { label: string; color: 'default' | 'info' | 'success' | 'error' | 'warning' }> = {
  draft: { label: 'Borrador', color: 'default' },
  sent: { label: 'Enviada', color: 'info' },
  accepted: { label: 'Aceptada', color: 'success' },
  rejected: { label: 'Rechazada', color: 'error' },
  expired: { label: 'Vencida', color: 'warning' },
  invoiced: { label: 'Facturada', color: 'success' },
};

// Lo que más se cotiza, para no teclear el precio cada vez. Salen de la lista vigente.
const ATAJOS: QuoteItem[] = [
  { description: 'GPS Driports 4G + instalación + 1 año de servicio', quantity: 1, unitPrice: 4990 },
  { description: 'GPS Driports 4G — precio de flota (5 o más unidades)', quantity: 5, unitPrice: 4500 },
  { description: 'Renovación anual del servicio de monitoreo', quantity: 1, unitPrice: 2500 },
  { description: 'Reinstalación del equipo en otro vehículo', quantity: 1, unitPrice: 1000 },
];

// Las listas se editan como texto, una línea por viñeta. Es lo que menos estorba: un editor
// de renglones con botoncitos para doce garantías se vuelve un formulario que nadie llena.
const aTexto = (v?: string[]) => (v || []).join('\n');
const aLista = (t: string) => t.split('\n').map(x => x.trim()).filter(Boolean);

const pesos = (n: number) =>
  `RD$${(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const aInputDate = (d: Date | string) => new Date(d).toISOString().slice(0, 10);

const abrirBlob = (blob: Blob) => window.open(window.URL.createObjectURL(blob), '_blank');

const nombreDe = (q: SavedQuote) => q.clientId?.name || q.prospect?.name || 'Sin destinatario';

// ─────────────────────────────────────────────────────────────────────────────

const QuoteGeneratorPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const avisar = (msg: string, seg = 5) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), seg * 1000);
  };

  // ── Destinatario ──
  const [destinatario, setDestinatario] = useState<'client' | 'prospect'>('prospect');
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [selectedClient, setSelectedClient] = useState<ClientOption | null>(null);
  const [prospect, setProspect] = useState({ name: '', company: '', phone: '', email: '', address: '' });

  // ── Documento ──
  const [quoteId, setQuoteId] = useState<string | null>(null);
  const [quoteStatus, setQuoteStatus] = useState<string>('');
  const [quoteNumber, setQuoteNumber] = useState('');
  const [items, setItems] = useState<QuoteItem[]>([]);
  const [descuentoPct, setDescuentoPct] = useState(0);
  const [descuentoLabel, setDescuentoLabel] = useState('');
  const [itbisOn, setItbisOn] = useState(false);
  const [itbisRate, setItbisRate] = useState(18);
  const [validUntil, setValidUntil] = useState(aInputDate(new Date(Date.now() + 15 * 86400000)));
  const [incluye, setIncluye] = useState('');
  const [garantia, setGarantia] = useState('');
  const [condiciones, setCondiciones] = useState('');
  const [datosPago, setDatosPago] = useState('');
  const [notas, setNotas] = useState('');

  const [templates, setTemplates] = useState<Template[]>([]);
  const [ocupado, setOcupado] = useState<string | null>(null);

  // ── Historial ──
  const [quotes, setQuotes] = useState<SavedQuote[]>([]);
  const [cargandoLista, setCargandoLista] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [dialogoFactura, setDialogoFactura] = useState<SavedQuote | null>(null);
  const [clienteParaFactura, setClienteParaFactura] = useState<ClientOption | null>(null);

  // ── Plantillas ──
  const [plantillaEnEdicion, setPlantillaEnEdicion] = useState<Template | null>(null);
  const [dialogoGuardarPlantilla, setDialogoGuardarPlantilla] = useState(false);
  const [nombrePlantillaNueva, setNombrePlantillaNueva] = useState('');

  const facturada = quoteStatus === 'invoiced';

  // Los totales se calculan igual que en el servidor y en el mismo orden —subtotal,
  // descuento, y el ITBIS sobre lo que queda— para que lo que se ve en pantalla sea lo que
  // sale impreso. Si aquí se calculara distinto, el cliente recibiría un PDF con otro total.
  const totales = useMemo(() => {
    const subtotal = items.reduce((s, it) => s + (it.quantity || 0) * (it.unitPrice || 0), 0);
    const descuento = descuentoPct > 0 ? Math.round(subtotal * (descuentoPct / 100) * 100) / 100 : 0;
    const base = Math.max(0, subtotal - descuento);
    const itbis = itbisOn ? Math.round(base * (itbisRate / 100) * 100) / 100 : 0;
    return { subtotal, descuento, base, itbis, total: base + itbis };
  }, [items, descuentoPct, itbisOn, itbisRate]);

  // ── Carga inicial ──
  useEffect(() => {
    apiService.getClients(1, 500)
      .then((res: any) => setClients(res?.data?.clients || res?.clients || []))
      .catch(() => {});

    apiService.getQuoteTemplates()
      .then((res: any) => {
        const lista: Template[] = res?.templates || [];
        setTemplates(lista);
        const base = lista.find(t => t.isDefault) || lista[0];
        if (base) aplicarPlantilla(base, true);
      })
      .catch(() => setError('No se pudieron cargar las plantillas'));

    apiService.getNextQuoteNumber()
      .then((res: any) => setQuoteNumber(res?.quoteNumber || ''))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarLista = useCallback(async () => {
    setCargandoLista(true);
    try {
      const params: any = { limit: 50 };
      if (busqueda) params.search = busqueda;
      if (filtroEstado) params.status = filtroEstado;
      const res: any = await apiService.getQuotes(1, 50, params);
      setQuotes(res?.quotes || []);
    } catch {
      setError('No se pudo cargar el historial');
    } finally {
      setCargandoLista(false);
    }
  }, [busqueda, filtroEstado]);

  useEffect(() => { if (tab === 1) cargarLista(); }, [tab, cargarLista]);

  // ── Plantillas ──

  /**
   * Vuelca una plantilla en el formulario. Con `soloTextos` no toca los renglones ni el
   * destinatario: cambiar de plantilla a mitad de una cotización no debe borrar lo que ya se
   * cotizó, solo el texto formal.
   */
  const aplicarPlantilla = (t: Template, soloTextos = false) => {
    setIncluye(aTexto(t.includes));
    setGarantia(aTexto(t.warranty));
    setCondiciones(aTexto(t.conditions));
    setDatosPago(t.paymentInfo || '');
    if (!soloTextos && t.notes) setNotas(t.notes);
    setItbisOn(!!t.itbisEnabled);
    setItbisRate(t.itbisRate || 18);
    setValidUntil(aInputDate(new Date(Date.now() + (t.validityDays || 15) * 86400000)));
    if (!soloTextos && t.items?.length) {
      setItems(t.items.map(i => ({ description: i.description, quantity: i.quantity || 1, unitPrice: i.unitPrice || 0 })));
    }
  };

  const guardarComoPlantilla = async () => {
    if (!nombrePlantillaNueva.trim()) return;
    setOcupado('plantilla');
    try {
      await apiService.createQuoteTemplate({
        name: nombrePlantillaNueva.trim(),
        description: 'Guardada desde una cotización',
        includes: aLista(incluye),
        warranty: aLista(garantia),
        conditions: aLista(condiciones),
        paymentInfo: datosPago,
        itbisEnabled: itbisOn,
        itbisRate,
        validityDays: Math.max(1, Math.round((new Date(validUntil).getTime() - Date.now()) / 86400000)) || 15,
      });
      const res: any = await apiService.getQuoteTemplates();
      setTemplates(res?.templates || []);
      setDialogoGuardarPlantilla(false);
      setNombrePlantillaNueva('');
      avisar('Plantilla guardada');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo guardar la plantilla');
    } finally {
      setOcupado(null);
    }
  };

  const guardarPlantillaEditada = async () => {
    if (!plantillaEnEdicion) return;
    setOcupado('plantilla');
    try {
      await apiService.updateQuoteTemplate(plantillaEnEdicion._id, plantillaEnEdicion);
      const res: any = await apiService.getQuoteTemplates();
      setTemplates(res?.templates || []);
      setPlantillaEnEdicion(null);
      avisar('Plantilla actualizada');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo actualizar la plantilla');
    } finally {
      setOcupado(null);
    }
  };

  const borrarPlantilla = async (t: Template) => {
    if (!window.confirm(`¿Borrar la plantilla "${t.name}"?`)) return;
    try {
      await apiService.deleteQuoteTemplate(t._id);
      setTemplates(prev => prev.filter(x => x._id !== t._id));
      avisar('Plantilla eliminada');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo borrar');
    }
  };

  // ── Renglones ──

  const cambiarItem = (i: number, campo: keyof QuoteItem, valor: any) => {
    setItems(prev => prev.map((it, idx) => (idx === i ? { ...it, [campo]: valor } : it)));
  };
  const agregarItem = (preset?: QuoteItem) =>
    setItems(prev => [...prev, preset ? { ...preset } : { description: '', quantity: 1, unitPrice: 0 }]);
  const quitarItem = (i: number) => setItems(prev => prev.filter((_, idx) => idx !== i));

  // ── El documento ──

  const cuerpo = () => ({
    quoteNumber,
    clientId: destinatario === 'client' ? selectedClient?._id : undefined,
    prospect: destinatario === 'prospect' ? prospect : undefined,
    items,
    discount: { rate: descuentoPct, label: descuentoLabel || undefined },
    itbis: { enabled: itbisOn, rate: itbisRate },
    validUntil,
    includes: aLista(incluye),
    warranty: aLista(garantia),
    conditions: aLista(condiciones),
    paymentInfo: datosPago,
    notes: notas,
  });

  const faltaDestinatario = destinatario === 'client' ? !selectedClient : !prospect.name.trim();

  const verBorrador = async () => {
    if (!items.length) return;
    setOcupado('borrador');
    setError(null);
    try {
      abrirBlob(await apiService.previewQuotePdf(cuerpo()));
      avisar('Borrador abierto. No se guardó nada: el número sigue libre.', 6);
    } catch (e: any) {
      setError('No se pudo generar el borrador');
    } finally {
      setOcupado(null);
    }
  };

  const guardar = async () => {
    if (!items.length || faltaDestinatario) return;
    setOcupado('guardar');
    setError(null);
    try {
      const res: any = await apiService.createQuote(cuerpo());
      setQuoteId(res.quote._id);
      setQuoteStatus(res.quote.status);
      setQuoteNumber(res.quote.quoteNumber);
      avisar(`Cotización ${res.quote.quoteNumber} guardada`);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo guardar');
    } finally {
      setOcupado(null);
    }
  };

  const guardarCambios = async () => {
    if (!quoteId) return;
    setOcupado('guardar');
    setError(null);
    try {
      await apiService.updateQuote(quoteId, cuerpo());
      avisar(`Cotización ${quoteNumber} actualizada`);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudieron guardar los cambios');
    } finally {
      setOcupado(null);
    }
  };

  const descargar = async () => {
    if (!quoteId) return;
    setOcupado('pdf');
    try {
      abrirBlob(await apiService.downloadQuotePdf(quoteId));
    } catch {
      setError('No se pudo descargar el PDF');
    } finally {
      setOcupado(null);
    }
  };

  const nueva = () => {
    setQuoteId(null);
    setQuoteStatus('');
    setItems([]);
    setSelectedClient(null);
    setProspect({ name: '', company: '', phone: '', email: '', address: '' });
    setDescuentoPct(0);
    setDescuentoLabel('');
    setNotas('');
    setError(null);
    const base = templates.find(t => t.isDefault) || templates[0];
    if (base) aplicarPlantilla(base, true);
    apiService.getNextQuoteNumber().then((r: any) => setQuoteNumber(r?.quoteNumber || '')).catch(() => {});
    setTab(0);
  };

  const abrir = (q: SavedQuote) => {
    setQuoteId(q._id);
    setQuoteStatus(q.status);
    setQuoteNumber(q.quoteNumber);
    setItems(q.items.map(i => ({ description: i.description, quantity: i.quantity, unitPrice: i.unitPrice })));
    setDescuentoPct(q.discount?.rate || 0);
    setDescuentoLabel(q.discount?.label || '');
    setItbisOn(!!q.itbis?.enabled);
    setItbisRate(q.itbis?.rate || 18);
    setValidUntil(aInputDate(q.validUntil));
    setIncluye(aTexto(q.includes));
    setGarantia(aTexto(q.warranty));
    setCondiciones(aTexto(q.conditions));
    setDatosPago(q.paymentInfo || '');
    setNotas(q.notes || '');
    if (q.clientId?._id) {
      setDestinatario('client');
      setSelectedClient(clients.find(c => c._id === q.clientId?._id) || null);
    } else {
      setDestinatario('prospect');
      setProspect({
        name: q.prospect?.name || '', company: q.prospect?.company || '',
        phone: q.prospect?.phone || '', email: q.prospect?.email || '',
        address: q.prospect?.address || '',
      });
    }
    setError(null);
    setTab(0);
  };

  const cambiarEstado = async (q: SavedQuote, status: string) => {
    try {
      await apiService.setQuoteStatus(q._id, status);
      if (quoteId === q._id) setQuoteStatus(status);
      avisar(`${q.quoteNumber}: ${ESTADOS[status]?.label.toLowerCase()}`);
      cargarLista();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo cambiar el estado');
    }
  };

  const convertir = async () => {
    if (!dialogoFactura) return;
    const q = dialogoFactura;
    const clientId = q.clientId?._id || clienteParaFactura?._id;
    if (!clientId) return;
    setOcupado('convertir');
    try {
      const res: any = await apiService.convertQuoteToInvoice(q._id, { clientId });
      setDialogoFactura(null);
      setClienteParaFactura(null);
      if (quoteId === q._id) setQuoteStatus('invoiced');
      avisar(`${res.message}. Ábrela en Facturas para cobrarla.`, 9);
      cargarLista();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo convertir en factura');
    } finally {
      setOcupado(null);
    }
  };

  const borrar = async (q: SavedQuote) => {
    if (!window.confirm(`¿Borrar la cotización ${q.quoteNumber} de ${nombreDe(q)}?\n\nEsto no se puede deshacer.`)) return;
    try {
      await apiService.deleteQuote(q._id);
      if (quoteId === q._id) nueva();
      avisar(`Cotización ${q.quoteNumber} eliminada`);
      cargarLista();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo borrar');
    }
  };

  // ───────────────────────────── Render ─────────────────────────────

  const campoDeLista = (label: string, ayuda: string, valor: string, set: (v: string) => void) => (
    <TextField
      label={label}
      value={valor}
      onChange={(e) => set(e.target.value)}
      helperText={`${ayuda} · ${aLista(valor).length} línea(s)`}
      fullWidth
      multiline
      minRows={4}
      maxRows={16}
      size="small"
      disabled={facturada}
    />
  );

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>Cotizaciones</Typography>
        {tab === 0 && (quoteId || items.length > 0) && (
          <Button size="small" onClick={nueva}>Empezar una nueva</Button>
        )}
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab icon={<QuoteIcon />} iconPosition="start" label="Nueva cotización" />
        <Tab icon={<HistoryIcon />} iconPosition="start" label="Cotizaciones" />
        <Tab icon={<TemplateIcon />} iconPosition="start" label="Plantillas" />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* ═══════════════ GENERADOR ═══════════════ */}
      {tab === 0 && (
        <>
          {quoteId && (
            <Alert severity={facturada ? 'success' : 'info'} sx={{ mb: 2 }}>
              {facturada
                ? `${quoteNumber} ya se convirtió en factura. No se edita: los cambios van en la factura.`
                : `Trabajando sobre ${quoteNumber} (${ESTADOS[quoteStatus]?.label || quoteStatus}). Lo que guardes modifica esta misma cotización.`}
            </Alert>
          )}

          {/* ── Destinatario ── */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>¿Para quién es?</Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={destinatario}
                  onChange={(_, v) => v && setDestinatario(v)}
                  disabled={facturada}
                >
                  <ToggleButton value="prospect">Alguien nuevo</ToggleButton>
                  <ToggleButton value="client">Cliente registrado</ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="caption" color="text.secondary">
                  Para convertirla en factura después hace falta que sea un cliente registrado.
                </Typography>
              </Stack>

              {destinatario === 'client' ? (
                <Autocomplete
                  options={clients}
                  getOptionLabel={(o) => o.name}
                  value={selectedClient}
                  onChange={(_, v) => setSelectedClient(v)}
                  isOptionEqualToValue={(o, v) => o._id === v._id}
                  disabled={facturada}
                  renderInput={(p) => <TextField {...p} label="Cliente" size="small" />}
                  sx={{ maxWidth: 480 }}
                />
              ) : (
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
                  <TextField label="Nombre" size="small" required disabled={facturada}
                    value={prospect.name} onChange={(e) => setProspect({ ...prospect, name: e.target.value })} sx={{ minWidth: 220 }} />
                  <TextField label="Empresa" size="small" disabled={facturada}
                    value={prospect.company} onChange={(e) => setProspect({ ...prospect, company: e.target.value })} sx={{ minWidth: 200 }} />
                  <TextField label="Teléfono" size="small" disabled={facturada}
                    value={prospect.phone} onChange={(e) => setProspect({ ...prospect, phone: e.target.value })} sx={{ minWidth: 160 }} />
                  <TextField label="Correo" size="small" disabled={facturada}
                    value={prospect.email} onChange={(e) => setProspect({ ...prospect, email: e.target.value })} sx={{ minWidth: 200 }} />
                  <TextField label="Dirección" size="small" disabled={facturada}
                    value={prospect.address} onChange={(e) => setProspect({ ...prospect, address: e.target.value })} sx={{ flexGrow: 1, minWidth: 240 }} />
                </Stack>
              )}
            </CardContent>
          </Card>

          {/* ── Encabezado del documento ── */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <TextField label="No. de cotización" size="small" value={quoteNumber}
                  onChange={(e) => setQuoteNumber(e.target.value)} sx={{ width: 170 }}
                  InputProps={{ readOnly: !!quoteId }} />
                <TextField label="Válida hasta" type="date" size="small" value={validUntil}
                  onChange={(e) => setValidUntil(e.target.value)} disabled={facturada}
                  InputLabelProps={{ shrink: true }} sx={{ width: 180 }} />
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>Plantilla de textos</InputLabel>
                  <Select
                    label="Plantilla de textos"
                    value=""
                    disabled={facturada}
                    onChange={(e) => {
                      const t = templates.find(x => x._id === e.target.value);
                      if (t) { aplicarPlantilla(t); avisar(`Textos de "${t.name}" aplicados`); }
                    }}
                  >
                    {templates.map(t => (
                      <MenuItem key={t._id} value={t._id}>
                        {t.name}{t.isDefault ? ' (por defecto)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button size="small" onClick={() => setDialogoGuardarPlantilla(true)} disabled={facturada}>
                  Guardar estos textos como plantilla
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {/* ── Renglones ── */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Qué se cotiza</Typography>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#1565C0' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Descripción</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, width: 90 }} align="center">Cant.</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, width: 140 }} align="center">Precio unitario</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, width: 130 }} align="right">Importe</TableCell>
                      <TableCell sx={{ color: 'white', width: 50 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                            Todavía no hay nada cotizado. Usa los atajos de abajo o agrega un renglón.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {items.map((it, i) => (
                      <TableRow key={i} sx={{ '&:nth-of-type(even)': { backgroundColor: '#F5F7FA' } }}>
                        <TableCell>
                          <TextField value={it.description} variant="standard" fullWidth size="small" disabled={facturada}
                            onChange={(e) => cambiarItem(i, 'description', e.target.value)} />
                        </TableCell>
                        <TableCell align="center">
                          <TextField value={it.quantity} variant="standard" type="number" size="small" disabled={facturada}
                            sx={{ width: 60 }} inputProps={{ min: 1, style: { textAlign: 'center' } }}
                            onChange={(e) => cambiarItem(i, 'quantity', Number(e.target.value))} />
                        </TableCell>
                        <TableCell align="center">
                          <TextField value={it.unitPrice} variant="standard" type="number" size="small" disabled={facturada}
                            sx={{ width: 110 }} inputProps={{ min: 0, style: { textAlign: 'center' } }}
                            onChange={(e) => cambiarItem(i, 'unitPrice', Number(e.target.value))} />
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={500}>{pesos((it.quantity || 0) * (it.unitPrice || 0))}</Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" color="error" disabled={facturada} onClick={() => quitarItem(i)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                <Button startIcon={<AddIcon />} size="small" onClick={() => agregarItem()} disabled={facturada}>
                  Renglón en blanco
                </Button>
                {ATAJOS.map((a, i) => (
                  <Chip key={i} label={`${a.description.split('—')[0].split('+')[0].trim()} · ${pesos(a.unitPrice)}`}
                    size="small" variant="outlined" onClick={() => agregarItem(a)} disabled={facturada} />
                ))}
              </Stack>

              <Divider sx={{ my: 2 }} />

              {/* ── Descuento, ITBIS y totales ── */}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-start">
                <Stack spacing={2} sx={{ flexGrow: 1, minWidth: 280 }}>
                  <Stack direction="row" spacing={2}>
                    <TextField label="Descuento" type="number" size="small" value={descuentoPct} disabled={facturada}
                      onChange={(e) => setDescuentoPct(Math.min(100, Math.max(0, Number(e.target.value))))}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      sx={{ width: 130 }} />
                    <TextField label="Cómo se llama el descuento" size="small" value={descuentoLabel} disabled={facturada}
                      onChange={(e) => setDescuentoLabel(e.target.value)}
                      placeholder="Descuento por volumen" sx={{ flexGrow: 1 }} />
                  </Stack>

                  <Paper variant="outlined" sx={{ p: 1.5, borderColor: itbisOn ? 'primary.main' : undefined }}>
                    <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
                      <FormControlLabel
                        control={<Switch checked={itbisOn} disabled={facturada}
                          onChange={(e) => setItbisOn(e.target.checked)} />}
                        label={<Typography sx={{ fontWeight: 600 }}>Cotizar con ITBIS</Typography>}
                      />
                      {itbisOn && (
                        <TextField label="Tasa" type="number" size="small" value={itbisRate} disabled={facturada}
                          onChange={(e) => setItbisRate(Math.max(0, Number(e.target.value)))}
                          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                          sx={{ width: 110 }} />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {itbisOn
                        ? `Se agrega ${itbisRate}% sobre ${pesos(totales.base)} y se imprime desglosado en el PDF.`
                        : 'Apagado: el PDF dice "Precios no incluyen ITBIS" y no suma impuesto.'}
                    </Typography>
                  </Paper>
                </Stack>

                <Paper variant="outlined" sx={{ p: 2, minWidth: 280 }}>
                  <Stack spacing={0.5}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">Subtotal</Typography>
                      <Typography variant="body2">{pesos(totales.subtotal)}</Typography>
                    </Stack>
                    {totales.descuento > 0 && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          {descuentoLabel || `Descuento (${descuentoPct}%)`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">− {pesos(totales.descuento)}</Typography>
                      </Stack>
                    )}
                    {itbisOn && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2">ITBIS ({itbisRate}%)</Typography>
                        <Typography variant="body2">{pesos(totales.itbis)}</Typography>
                      </Stack>
                    )}
                    <Divider sx={{ my: 1 }} />
                    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                      <Typography sx={{ fontWeight: 700 }}>TOTAL</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#1565C0' }}>
                        {pesos(totales.total)}
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              </Stack>
            </CardContent>
          </Card>

          {/* ── El cuerpo formal ── */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Lo que se promete por escrito
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Una línea por viñeta. Esto es lo que después sostiene o hunde un reclamo, así que
                sale impreso tal cual se escriba aquí.
              </Typography>

              <Stack spacing={2}>
                {campoDeLista('Qué incluye el servicio', 'Lo que el cliente recibe', incluye, setIncluye)}
                {campoDeLista('Garantía', 'Qué se garantiza y qué no', garantia, setGarantia)}
                {campoDeLista('Condiciones', 'Términos comerciales', condiciones, setCondiciones)}

                <TextField label="Datos de pago" value={datosPago} onChange={(e) => setDatosPago(e.target.value)}
                  fullWidth multiline minRows={2} size="small" disabled={facturada} />
                <TextField label="Notas (opcional)" value={notas} onChange={(e) => setNotas(e.target.value)}
                  fullWidth multiline minRows={2} size="small" disabled={facturada} />
              </Stack>
            </CardContent>
          </Card>

          {/* ── Acciones ── */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
            <Button variant="outlined" size="large" startIcon={<PdfIcon />} onClick={verBorrador}
              disabled={!items.length || ocupado === 'borrador'}
              title="Genera el PDF sin guardar nada ni gastar un número de la serie">
              {ocupado === 'borrador' ? 'Generando...' : 'Ver borrador'}
            </Button>

            {!quoteId && (
              <Button variant="contained" size="large"
                startIcon={ocupado === 'guardar' ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={guardar} disabled={!items.length || faltaDestinatario || ocupado === 'guardar'} sx={{ px: 4 }}>
                Guardar cotización
              </Button>
            )}

            {quoteId && !facturada && (
              <Button variant="outlined" size="large"
                startIcon={ocupado === 'guardar' ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={guardarCambios} disabled={!items.length || ocupado === 'guardar'}>
                Guardar cambios
              </Button>
            )}

            {quoteId && (
              <Button variant="contained" size="large" startIcon={<PdfIcon />} onClick={descargar}
                disabled={ocupado === 'pdf'} sx={{ px: 4 }}>
                Descargar PDF
              </Button>
            )}
          </Box>

          {faltaDestinatario && items.length > 0 && !quoteId && (
            <Typography variant="caption" color="error" display="block" align="right" sx={{ mt: 1 }}>
              Falta decir para quién es la cotización.
            </Typography>
          )}
        </>
      )}

      {/* ═══════════════ HISTORIAL ═══════════════ */}
      {tab === 1 && (
        <Card>
          <CardContent>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
              <TextField label="Buscar" size="small" value={busqueda} sx={{ width: 240 }}
                onChange={(e) => setBusqueda(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && cargarLista()}
                placeholder="Número, nombre, empresa o teléfono" />
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>Estado</InputLabel>
                <Select label="Estado" value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
                  <MenuItem value="">Todas</MenuItem>
                  {Object.entries(ESTADOS).map(([k, v]) => (
                    <MenuItem key={k} value={k}>{v.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="outlined" onClick={cargarLista} disabled={cargandoLista}>
                {cargandoLista ? <CircularProgress size={18} /> : 'Buscar'}
              </Button>
            </Stack>

            {cargandoLista ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : quotes.length === 0 ? (
              <Alert severity="info">Todavía no hay cotizaciones guardadas.</Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#1565C0' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>No.</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Para</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">Total</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }} align="center">ITBIS</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Estado</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Válida hasta</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, width: 200 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {quotes.map((q) => (
                      <TableRow key={q._id} hover sx={{ '&:nth-of-type(even)': { backgroundColor: '#F5F7FA' } }}>
                        <TableCell><Typography fontWeight={600}>{q.quoteNumber}</Typography></TableCell>
                        <TableCell>
                          {nombreDe(q)}
                          {q.prospect?.company && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {q.prospect.company}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right"><Typography fontWeight={500}>{pesos(q.total)}</Typography></TableCell>
                        <TableCell align="center">
                          {q.itbis?.enabled
                            ? <Chip size="small" label={`${q.itbis.rate}%`} color="primary" variant="outlined" />
                            : <Typography variant="caption" color="text.secondary">—</Typography>}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={ESTADOS[q.status]?.label || q.status}
                            color={ESTADOS[q.status]?.color || 'default'} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color={q.vencida ? 'error' : 'text.primary'}>
                            {new Date(q.validUntil).toLocaleDateString('es-DO')}
                            {q.vencida && ' (vencida)'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title="Descargar PDF">
                              <IconButton size="small" color="primary"
                                onClick={async () => { try { abrirBlob(await apiService.downloadQuotePdf(q._id)); } catch { setError('No se pudo descargar'); } }}>
                                <PdfIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={q.status === 'invoiced' ? 'Ver (ya facturada)' : 'Seguir trabajándola'}>
                              <IconButton size="small" onClick={() => abrir(q)}><EditIcon fontSize="small" /></IconButton>
                            </Tooltip>
                            {q.status !== 'invoiced' && (
                              <>
                                <Tooltip title="Marcar como enviada">
                                  <IconButton size="small" onClick={() => cambiarEstado(q, 'sent')}>
                                    <SendIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="La aceptaron">
                                  <IconButton size="small" color="success" onClick={() => cambiarEstado(q, 'accepted')}>
                                    <AcceptIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="La rechazaron">
                                  <IconButton size="small" color="warning" onClick={() => cambiarEstado(q, 'rejected')}>
                                    <RejectIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Convertir en factura">
                                  <IconButton size="small" color="success"
                                    onClick={() => { setDialogoFactura(q); setClienteParaFactura(null); }}>
                                    <InvoiceIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Borrar">
                                  <IconButton size="small" color="error" onClick={() => borrar(q)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* ═══════════════ PLANTILLAS ═══════════════ */}
      {tab === 2 && (
        <Stack spacing={2}>
          <Alert severity="info">
            Las plantillas guardan el texto formal que se repite: qué incluye, garantías y condiciones.
            Al abrir una cotización nueva se carga la marcada como <strong>por defecto</strong>.
          </Alert>

          {templates.map((t) => (
            <Card key={t._id}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography sx={{ fontWeight: 700 }}>
                      {t.name} {t.isDefault && <Chip size="small" label="por defecto" color="primary" sx={{ ml: 1 }} />}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t.includes.length} incluye · {t.warranty.length} garantías · {t.conditions.length} condiciones ·
                      validez {t.validityDays} días · ITBIS {t.itbisEnabled ? `${t.itbisRate}% encendido` : 'apagado'}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" startIcon={<EditIcon />} onClick={() => setPlantillaEnEdicion({ ...t })}>
                      Editar
                    </Button>
                    <IconButton size="small" color="error" onClick={() => borrarPlantilla(t)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* ── Diálogo: guardar textos como plantilla ── */}
      <Dialog open={dialogoGuardarPlantilla} onClose={() => setDialogoGuardarPlantilla(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Guardar estos textos como plantilla</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Se guardan el "qué incluye", las garantías, las condiciones, los datos de pago, la
            validez y el ITBIS. No se guardan los renglones ni el destinatario.
          </Typography>
          <TextField autoFocus fullWidth label="Nombre de la plantilla" value={nombrePlantillaNueva}
            onChange={(e) => setNombrePlantillaNueva(e.target.value)} placeholder="Flota 10+, Renovación, Gobierno..." />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogoGuardarPlantilla(false)}>Cancelar</Button>
          <Button variant="contained" onClick={guardarComoPlantilla}
            disabled={!nombrePlantillaNueva.trim() || ocupado === 'plantilla'}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Diálogo: editar plantilla ── */}
      <Dialog open={!!plantillaEnEdicion} onClose={() => setPlantillaEnEdicion(null)} maxWidth="md" fullWidth>
        <DialogTitle>Editar plantilla</DialogTitle>
        <DialogContent>
          {plantillaEnEdicion && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <TextField label="Nombre" size="small" value={plantillaEnEdicion.name}
                  onChange={(e) => setPlantillaEnEdicion({ ...plantillaEnEdicion, name: e.target.value })} sx={{ minWidth: 220 }} />
                <TextField label="Validez (días)" type="number" size="small" value={plantillaEnEdicion.validityDays}
                  onChange={(e) => setPlantillaEnEdicion({ ...plantillaEnEdicion, validityDays: Number(e.target.value) })} sx={{ width: 140 }} />
                <FormControlLabel
                  control={<Switch checked={plantillaEnEdicion.itbisEnabled}
                    onChange={(e) => setPlantillaEnEdicion({ ...plantillaEnEdicion, itbisEnabled: e.target.checked })} />}
                  label="ITBIS por defecto" />
                <FormControlLabel
                  control={<Switch checked={plantillaEnEdicion.isDefault}
                    onChange={(e) => setPlantillaEnEdicion({ ...plantillaEnEdicion, isDefault: e.target.checked })} />}
                  label="Usar por defecto" />
              </Stack>

              <TextField label="Qué incluye el servicio" fullWidth multiline minRows={5}
                value={aTexto(plantillaEnEdicion.includes)}
                onChange={(e) => setPlantillaEnEdicion({ ...plantillaEnEdicion, includes: aLista(e.target.value) })}
                helperText="Una línea por viñeta" />
              <TextField label="Garantía" fullWidth multiline minRows={4}
                value={aTexto(plantillaEnEdicion.warranty)}
                onChange={(e) => setPlantillaEnEdicion({ ...plantillaEnEdicion, warranty: aLista(e.target.value) })} />
              <TextField label="Condiciones" fullWidth multiline minRows={4}
                value={aTexto(plantillaEnEdicion.conditions)}
                onChange={(e) => setPlantillaEnEdicion({ ...plantillaEnEdicion, conditions: aLista(e.target.value) })} />
              <TextField label="Datos de pago" fullWidth multiline minRows={2}
                value={plantillaEnEdicion.paymentInfo || ''}
                onChange={(e) => setPlantillaEnEdicion({ ...plantillaEnEdicion, paymentInfo: e.target.value })} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPlantillaEnEdicion(null)}>Cancelar</Button>
          <Button variant="contained" onClick={guardarPlantillaEditada} disabled={ocupado === 'plantilla'}>
            Guardar plantilla
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Diálogo: convertir en factura ── */}
      <Dialog open={!!dialogoFactura} onClose={() => !ocupado && setDialogoFactura(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Convertir {dialogoFactura?.quoteNumber} en factura</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Aquí la oferta pasa a ser cobro: se emite una factura con estos mismos renglones y el
            mismo ITBIS, y la cotización queda cerrada. Cobrarla se hace después desde Facturas.
          </Alert>

          {dialogoFactura && (
            <Stack spacing={1} sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Para:</strong> {nombreDe(dialogoFactura)}
              </Typography>
              <Typography variant="body2">
                <strong>Total:</strong> {pesos(dialogoFactura.total)}
                {dialogoFactura.itbis?.enabled ? ` (incluye ITBIS ${dialogoFactura.itbis.rate}%)` : ' (sin ITBIS)'}
              </Typography>
            </Stack>
          )}

          {!dialogoFactura?.clientId?._id && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Esta cotización se le hizo a alguien que no está registrado como cliente. Elige a
                qué cliente se le factura, o regístralo primero en Clientes.
              </Alert>
              <Autocomplete
                options={clients}
                getOptionLabel={(o) => o.name}
                value={clienteParaFactura}
                onChange={(_, v) => setClienteParaFactura(v)}
                isOptionEqualToValue={(o, v) => o._id === v._id}
                renderInput={(p) => <TextField {...p} label="Facturar a" size="small" />}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogoFactura(null)} disabled={!!ocupado}>Cancelar</Button>
          <Button variant="contained" color="success" onClick={convertir}
            disabled={ocupado === 'convertir' || (!dialogoFactura?.clientId?._id && !clienteParaFactura)}
            startIcon={ocupado === 'convertir' ? <CircularProgress size={18} color="inherit" /> : <InvoiceIcon />}>
            Emitir factura
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuoteGeneratorPage;
